import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const constructionTypeKeys = {
  all: ['constructionTypes'],
  lists: () => [...constructionTypeKeys.all, 'list'],
  details: () => [...constructionTypeKeys.all, 'detail'],
  detail: (id) => [...constructionTypeKeys.details(), id],
};

export const useConstructionTypes = () => {
  return useQuery({
    queryKey: constructionTypeKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('construction_types');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useConstructionType = (id, options = {}) => {
  return useQuery({
    queryKey: constructionTypeKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`construction_types/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useCreateConstructionType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (constructionTypeData) => {
      const response = await apiClient.post('construction_types', constructionTypeData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: constructionTypeKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating construction type:', error);
    },
  });
};

export const useUpdateConstructionType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`construction_types/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(constructionTypeKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: constructionTypeKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating construction type:', error);
    },
  });
};

export const useDeleteConstructionType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`construction_types/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: constructionTypeKeys.lists() });
      queryClient.removeQueries({ queryKey: constructionTypeKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting construction type:', error);
    },
  });
};
